import type { Color, IconName } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// Field type metadata — single source of truth for Lucide icons, labels, and
// colors for each custom field type.
// ---------------------------------------------------------------------------

export type FieldTypeMeta = {
  label: string;
  icon: IconName;
  /** A name on the shared scale — the same vocabulary `Icon`/`Text` `color=`,
   *  `Badge color=` and the entity-selector avatar config all take. */
  color: Color;
};

/**
 * Stable ordering for display (e.g. grouped tables, picker dialogs).
 * Kept separate from the record so iteration order is explicit.
 */
export const FIELD_TYPE_ORDER: string[] = [
  "text",
  "paragraph",
  "number",
  "money",
  "select",
  "email",
  "phone",
  "url",
  "checkbox",
  "time",
  "date",
  "dateTime",
  "address",
  "member",
  "photos",
];

/** Lucide icon + design-system color for every custom field type. */
export const FIELD_TYPE_META: Record<string, FieldTypeMeta> = {
  text: { label: "Text", icon: "type", color: "dark-grey" },
  paragraph: { label: "Textarea", icon: "text-cursor-input", color: "green" },
  number: { label: "Number", icon: "hash", color: "dark-grey" },
  money: { label: "Money", icon: "dollar-sign", color: "dark-grey" },
  select: { label: "Dropdown", icon: "circle-arrow-down", color: "blue" },
  email: { label: "Email", icon: "at-sign", color: "green" },
  phone: { label: "Phone", icon: "phone", color: "dark-grey" },
  url: { label: "Website", icon: "globe", color: "blue" },
  checkbox: { label: "Checkbox", icon: "square-check", color: "blue" },
  time: { label: "Time", icon: "hourglass", color: "dark-grey" },
  date: { label: "Date", icon: "calendar", color: "red" },
  dateTime: { label: "Date & Time", icon: "calendar-clock", color: "red" },
  address: { label: "Location", icon: "map-pin", color: "green" },
  member: { label: "Member", icon: "user", color: "pink" },
  photos: { label: "Photos", icon: "image", color: "purple" },
  files: { label: "Files", icon: "file-text", color: "dark-grey" },
  region: { label: "Region", icon: "map-pin", color: "blue" },
};

// The icon registry names lucide's `FileQuestionMark` `file-question-mark`.
const FALLBACK_META: FieldTypeMeta = { label: "Unknown", icon: "file-question-mark", color: "muted" };

export function getFieldTypeMeta(type: string): FieldTypeMeta {
  return FIELD_TYPE_META[type] ?? FALLBACK_META;
}

/** @deprecated Use `FIELD_TYPE_META` and access `.icon` instead. */
export const FIELD_TYPE_ICONS: Record<string, IconName> = Object.fromEntries(
  Object.entries(FIELD_TYPE_META).map(([k, v]) => [k, v.icon]),
);
